import { PieceType, PieceColor } from '../types/chess';
// Importing Chess and Piece from chess.js. The consumer must have chess.js in scope.
// eslint-disable-next-line
import { Chess, Move, Piece, PieceSymbol, Color, Square } from 'chess.js';

/**
 * Generates a random chess position as a FEN string.
 * Ensures both kings are present and not adjacent, and piece counts are legal.
 * @returns {string} A valid FEN string representing a random position
 */
export function randomizePositions(): string {
  const chess = new Chess(); // Start with empty board
  chess.clear();
  const pieces: PieceType[] = ['p', 'n', 'b', 'r', 'q', 'k'];
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const ranks = ['1', '2', '3', '4', '5', '6', '7', '8'];

  // Always place kings (required for valid position)
  // Ensure kings are not on adjacent squares to avoid invalid positions
  const whiteKingFile = files[Math.floor(Math.random() * 8)];
  const whiteKingRank = ranks[Math.floor(Math.random() * 8)];

  // Place black king away from white king
  let blackKingFile: string, blackKingRank: string;
  do {
    blackKingFile = files[Math.floor(Math.random() * 8)];
    blackKingRank = ranks[Math.floor(Math.random() * 8)];
  } while (
    Math.abs(files.indexOf(blackKingFile) - files.indexOf(whiteKingFile)) <= 1 &&
    Math.abs(ranks.indexOf(blackKingRank) - ranks.indexOf(whiteKingRank)) <= 1
  );

  // Place kings
  const whiteKingLabel = `${whiteKingFile}${whiteKingRank}`;
  const blackKingLabel = `${blackKingFile}${blackKingRank}`;
  chess.put({ type: 'k', color: 'w' }, whiteKingLabel as Square);
  chess.put({ type: 'k', color: 'b' }, blackKingLabel as Square);

  // Add random pieces (excluding kings which we've already placed)
  const otherPieces = pieces.filter(p => p !== 'k');
  const numPieces = 5 + Math.floor(Math.random() * 20); // Between 5-25 additional pieces

  for (let i = 0; i < numPieces; i++) {
    const piece = otherPieces[Math.floor(Math.random() * otherPieces.length)];
    const color: PieceColor = Math.random() > 0.5 ? 'w' : 'b';
    const file = files[Math.floor(Math.random() * 8)];
    const rank = ranks[Math.floor(Math.random() * 8)];
    const square = `${file}${rank}`;

    // Check if square is empty before placing piece
    if (!chess.get(square as Square)) {
      // Check piece count limits based on standard chess setup
      const pieceCount = chess.board().flat().filter((p: Piece | null) =>
        p && p.type === piece && p.color === color
      ).length;

      const maxCount: Record<PieceType, number> = {
        p: 8,  // pawns
        n: 2,  // knights
        b: 2,  // bishops
        r: 2,  // rooks
        q: 1,  // queen
        k: 1   // king (already placed)
      };
      if (pieceCount < maxCount[piece]) {
        const old_fen = chess.fen();
        try {
          chess.put({ type: piece, color }, square as Square);
          chess.load(chess.fen());
        } catch (error) {
          // If invalid, revert
          chess.load(old_fen);
        }
      }
    }
  }

  return chess.fen();
}

/**
 * Generates a random SAN move from a random position and piece set.
 * @param usedPositions - Set of used SANs to avoid repeats
 * @param pieces - Array of PieceSymbols to use (optional)
 * @param testModeProps - Optional test mode props
 * @returns A random SAN move
 */
export function generateRandomSan(
  usedPositions: Set<string>,
  pieces?: string[],
  testModeProps?: { initialSAN?: string[]; score?: number },
  skipBadStateCheck?: boolean
): { fen: string; san: string } {
  const { Chess } = require('chess.js');
  let attempts = 0;
  let san: string | undefined = undefined;
  let fen: string | undefined = undefined;
  let possiblePieces = pieces ? [...pieces] : ['p', 'n', 'b', 'r', 'q', 'k'];

  // For test mode, use provided SANs
  if (testModeProps?.initialSAN && typeof testModeProps.score === 'number' && testModeProps.initialSAN.length > testModeProps.score) {
    return { fen: '', san: testModeProps.initialSAN[testModeProps.score] };
  }

  const MAX_TRIES = 1000;

  const badState = (chess: any) => {
    const isCheckmate = chess.isCheckmate();
    const isStalemate = chess.isStalemate();
    const isDraw = chess.isDraw();
    const isCheck = chess.isCheck();
    const isGameOver = chess.isGameOver();

    console.log('isCheckmate', isCheckmate);
    console.log('isStalemate', isStalemate);
    console.log('isDraw', isDraw);
    console.log('isCheck', isCheck);
    console.log('isGameOver', isGameOver);

    return (
      isCheckmate ||
      isStalemate ||
      isDraw ||
      isCheck ||
      isGameOver
    );
  };

  // Try up to 100 times to generate a valid SAN
  while (attempts < MAX_TRIES) {
    const piece = possiblePieces[Math.floor(Math.random() * possiblePieces.length)];
    const randomFen = randomizePositions();
    const chess = new Chess(randomFen);
    if (badState(chess) && !skipBadStateCheck) {
      attempts++;
      continue;
    }
    // Get all moves for the piece
    const moves = chess.moves({ piece, verbose: true });
    if (moves.length === 0) {
      attempts++;
      continue;
    } else {
      const move = moves[Math.floor(Math.random() * moves.length)];
      san = move.san;
      fen = chess.fen();
      const movedChess : Chess = new Chess(fen);
      if (!san) {
        attempts++;
        continue;
      }
      movedChess.move(san);
      if ((badState(movedChess) 
        && !skipBadStateCheck)
        || usedPositions.has(san)) {
        attempts++;
        continue;
      }
      break;
    }
  }
  if (attempts >= MAX_TRIES || !san || !fen) {
    throw new Error('generateRandomSan: Failed to generate a valid SAN after 20 attempts.');
  }
  return { fen, san };
} 